import { SELECTORS, CLASS_NAMES } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/** Fallback ratio (16 / 9) used until real dimensions are known */
const DEFAULT_RATIO = 16 / 9;

/** `data-vt-type` value marking a trigger as an oEmbed (provider) video */
const TYPE_EMBED = 'embed';

/** Matches the video ID in any YouTube embed / share / shorts URL */
const YOUTUBE_ID_PATTERN =
  /(?:youtube(?:-nocookie)?\.com\/(?:embed|shorts|v)\/|youtu\.be\/)([\w-]{6,})/;

/**
 * Poster frames YouTube exposes per video, best first. `maxresdefault` is only
 * generated for uploads above 720p and `hq720` only for widescreen sources, so
 * both can 404; `mqdefault` always exists. Every entry here is a true 16:9
 * crop — `hqdefault` and `sddefault` are 4:3 and would letterbox the tile.
 */
const YOUTUBE_POSTERS = ['maxresdefault', 'hq720', 'mqdefault'];

/** Permissions handed to the injected provider iframe */
const EMBED_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';

/**
 * A class for the Videos + Text module. Wires each video block up to a shared
 * lightbox: clicking a block (or its play button) opens the video in a
 * fullscreen overlay, locks background scrolling, and plays it. The lightbox
 * closes via the close button, the backdrop, or the Escape key.
 *
 * Two kinds of block feed the same lightbox. A `file` block points at a
 * self-hosted MP4 and plays through the native `<video>` element. An `embed`
 * block carries an ACF oEmbed payload (YouTube, Vimeo) parked inside an inert
 * `<template>`; on activation its iframe `src` is read out, given an autoplay
 * flag and re-created inside the lightbox. Parking the markup in a template
 * matters: left in the document, every provider iframe would boot a player on
 * page load.
 *
 * Because that template renders nothing, embed blocks would otherwise sit in
 * the grid as flat colour while file blocks show a frame of their video. The
 * module closes that gap on init by deriving each embed's poster from its video
 * ID and injecting it using the same preview-image markup the file variant
 * uses, so both kinds of tile read identically.
 *
 * The lightbox sizes itself to the intrinsic aspect ratio of whichever video is
 * playing, so portrait, square and landscape sources all render edge-to-edge
 * with their native controls hugging the video rather than a fixed 16:9 box.
 * File sources report this through `loadedmetadata`; embeds report it through
 * the width / height attributes the provider returns with the oEmbed markup.
 *
 * On init the lightbox is relocated to `<body>`. Any ancestor carrying a
 * `transform`, `filter`, `perspective`, `will-change` or `contain` becomes the
 * containing block for `position: fixed` descendants, which pins the overlay
 * inside the component's own box and lets later sections (the footer) paint
 * over it. Reparenting to `<body>` restores viewport-relative positioning
 * without touching markup outside this component.
 */
export default class VideosAndText extends MessageBus {
  /**
   * Reports whether a trigger plays a provider embed rather than a file.
   * @param {HTMLElement} trigger The trigger block to test
   * @returns {boolean} True when the block carries an oEmbed payload
   */
  static isEmbed(trigger) {
    return trigger.getAttribute('data-vt-type') === TYPE_EMBED;
  }

  /**
   * Reads the video source for a trigger from its nested `<source>` element.
   * @param {HTMLElement} trigger The trigger block that was activated
   * @returns {?string} The video URL, or null if none is found
   */
  static getVideoSrc(trigger) {
    const source = trigger.querySelector(
      SELECTORS.VIDEOS_AND_TEXT_VIDEO_SOURCE
    );

    return source ? source.getAttribute('src') : null;
  }

  /**
   * Pulls the provider iframe out of a trigger's inert `<template>`. The
   * element returned belongs to the template's document fragment, so it is
   * never live and never loads — it is read for its attributes only.
   * @param {HTMLElement} trigger The trigger block that was activated
   * @returns {?HTMLIFrameElement} The template's iframe, or null if absent
   */
  static getEmbedFrame(trigger) {
    const template = trigger.querySelector(SELECTORS.VIDEOS_AND_TEXT_EMBED);

    if (!template) return null;

    const root = template.content || template;

    return root.querySelector('iframe');
  }

  /**
   * Adds the playback flags the lightbox needs to a provider URL. Unknown
   * params are ignored by providers, so one call covers YouTube and Vimeo.
   * @param {?string} src The iframe source returned by the oEmbed field
   * @returns {?string} The source with autoplay applied, or null if empty
   */
  static withAutoplay(src) {
    if (!src) return null;

    try {
      const url = new URL(src, window.location.href);

      url.searchParams.set('autoplay', '1');
      url.searchParams.set('playsinline', '1');
      url.searchParams.set('rel', '0');

      return url.toString();
    } catch (error) {
      return src;
    }
  }

  /**
   * Extracts the YouTube video ID from a provider URL, used to derive the
   * poster frame an embed block shows in the grid.
   * @param {?string} src The iframe source returned by the oEmbed field
   * @returns {?string} The video ID, or null for non-YouTube sources
   */
  static getYouTubeId(src) {
    const match = src ? src.match(YOUTUBE_ID_PATTERN) : null;

    return match ? match[1] : null;
  }

  /**
   * Builds the URL for one rung of the poster fallback ladder.
   * @param {string} id The YouTube video ID
   * @param {number} step Index into the poster list, best quality first
   * @returns {string} The thumbnail URL
   */
  static getPosterSrc(id, step) {
    return `https://i.ytimg.com/vi/${id}/${YOUTUBE_POSTERS[step]}.jpg`;
  }

  /**
   * Reads the intrinsic width / height ratio from a `<video>` element, if its
   * metadata has already been parsed.
   * @param {?HTMLVideoElement} video The video element to measure
   * @returns {?number} The ratio, or null when dimensions are not yet known
   */
  static getRatio(video) {
    if (!video || !video.videoWidth || !video.videoHeight) return null;

    return video.videoWidth / video.videoHeight;
  }

  /**
   * Reads the ratio a provider declared on its oEmbed iframe. Embeds cannot be
   * measured after the fact — the iframe is cross-origin — so these attributes
   * are the only dimensions available.
   * @param {?HTMLIFrameElement} frame The iframe from the trigger's template
   * @returns {?number} The ratio, or null when the attributes are missing
   */
  static getEmbedRatio(frame) {
    if (!frame) return null;

    const width = parseFloat(frame.getAttribute('width'));
    const height = parseFloat(frame.getAttribute('height'));

    if (!width || !height) return null;

    return width / height;
  }

  /**
   * Constructor function of the VideosAndText class. Must receive one argument,
   * a DOM node representing the module's container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node that is passed into the constructor
     */
    this.element = element;

    /**
     * Scroll position captured when the lightbox opens, restored on close
     * @property {number} scrollY The window scroll offset at open time
     */
    this.scrollY = 0;

    /**
     * The trigger that last opened the lightbox, so focus can be restored
     * @property {?HTMLElement} lastFocused The element focused before opening
     */
    this.lastFocused = null;

    /**
     * Placeholder marking where the lightbox sat before it was moved to body,
     * so `destroy()` can put it back where the template rendered it
     * @property {?Comment} lightboxAnchor The original DOM position marker
     */
    this.lightboxAnchor = null;

    /**
     * Poster elements this module injected, tracked so teardown can remove them
     * @property {HTMLElement[]} posters The generated preview-image wrappers
     */
    this.posters = [];

    this.init();
  }

  /**
   * Initializes the view by calling the functions to create DOM references,
   * setup event handlers, give the embed blocks their poster frames, move the
   * lightbox out of any transformed ancestor and then bind the handlers to the
   * view.
   * @returns {VideosAndText} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences()
      .setupHandlers()
      .renderPosters()
      .portalLightbox()
      .enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {VideosAndText} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.triggers = Array.from(
      this.element.querySelectorAll(SELECTORS.VIDEOS_AND_TEXT_TRIGGER)
    );
    this.lightbox = this.element.querySelector(
      SELECTORS.VIDEOS_AND_TEXT_LIGHTBOX
    );
    this.lightboxVideo = this.element.querySelector(
      SELECTORS.VIDEOS_AND_TEXT_LIGHTBOX_VIDEO
    );
    this.lightboxEmbed = this.element.querySelector(
      SELECTORS.VIDEOS_AND_TEXT_LIGHTBOX_EMBED
    );
    this.closeEls = Array.from(
      this.element.querySelectorAll(SELECTORS.VIDEOS_AND_TEXT_LIGHTBOX_CLOSE)
    );

    return this;
  }

  /**
   * Builds the poster image for one embed block. The wrapper reuses the file
   * variant's preview-image class, so it inherits that element's radius, crop
   * and z-index and sits under the existing play-button overlay untouched.
   * @param {string} id The YouTube video ID to pull the poster from
   * @returns {HTMLElement} The poster wrapper, ready to insert
   */
  static createPoster(id) {
    const poster = document.createElement('div');
    const image = document.createElement('img');
    let step = 0;

    poster.className = CLASS_NAMES.VIDEOS_AND_TEXT_PREVIEW_IMAGE;
    poster.setAttribute('data-vt-thumb', '');

    image.setAttribute('alt', ''); // Decorative: the trigger carries the label
    image.setAttribute('loading', 'lazy');
    image.setAttribute('decoding', 'async');

    // Walk down the quality ladder on each 404, and drop the poster entirely if
    // every rung fails so the block falls back to its flat background.
    image.addEventListener('error', () => {
      step += 1;

      if (step >= YOUTUBE_POSTERS.length) {
        poster.remove();
        return;
      }

      image.setAttribute('src', VideosAndText.getPosterSrc(id, step));
    });

    image.setAttribute('src', VideosAndText.getPosterSrc(id, step));
    poster.appendChild(image);

    return poster;
  }

  /**
   * Gives every embed block a poster frame taken from its own video, so the
   * grid shows the video rather than empty colour. Blocks that already carry a
   * poster are left alone, as are non-YouTube providers, whose thumbnails are
   * not derivable from the embed URL.
   * @returns {VideosAndText} A reference to the current instance of the class
   * @chainable
   */
  renderPosters() {
    this.triggers.forEach((trigger) => {
      if (!VideosAndText.isEmbed(trigger)) return;
      if (trigger.querySelector(SELECTORS.VIDEOS_AND_TEXT_THUMB)) return;

      const frame = VideosAndText.getEmbedFrame(trigger);
      const id = frame
        ? VideosAndText.getYouTubeId(frame.getAttribute('src'))
        : null;

      if (!id) return;

      const poster = VideosAndText.createPoster(id);

      trigger.insertBefore(poster, trigger.firstChild);
      this.posters.push(poster);
    });

    return this;
  }

  /**
   * Removes the poster elements this module generated, leaving the markup as
   * the template rendered it.
   * @returns {VideosAndText} A reference to the current instance of the class
   * @chainable
   */
  clearPosters() {
    this.posters.forEach((poster) => poster.remove());
    this.posters = [];

    return this;
  }

  /**
   * Moves the lightbox to `<body>` so `position: fixed` resolves against the
   * viewport rather than a transformed ancestor. DOM references are cached
   * before this runs, so the move does not affect element lookups.
   * @returns {VideosAndText} A reference to the current instance of the class
   * @chainable
   */
  portalLightbox() {
    if (!this.lightbox || this.lightbox.parentNode === document.body) {
      return this;
    }

    this.lightboxAnchor = document.createComment('videos-and-text lightbox');
    this.lightbox.parentNode.insertBefore(this.lightboxAnchor, this.lightbox);
    document.body.appendChild(this.lightbox);

    return this;
  }

  /**
   * Returns the lightbox to its original position in the component markup.
   * @returns {VideosAndText} A reference to the current instance of the class
   * @chainable
   */
  restoreLightbox() {
    const anchor = this.lightboxAnchor;

    if (!this.lightbox || !anchor || !anchor.parentNode) return this;

    anchor.parentNode.replaceChild(this.lightbox, anchor);
    this.lightboxAnchor = null;

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {VideosAndText} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleTriggerClick = this.onTriggerClick.bind(this);
    this.handleTriggerKeydown = this.onTriggerKeydown.bind(this);
    this.handleClose = this.close.bind(this);
    this.handleDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.handleLightboxMetadata = this.onLightboxMetadata.bind(this);

    return this;
  }

  /**
   * Attach the event listeners to enable interaction with the view.
   * @returns {VideosAndText} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (!this.lightbox || !this.lightboxVideo || !this.triggers.length) {
      return this;
    }

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleTriggerClick);
      trigger.addEventListener('keydown', this.handleTriggerKeydown);
    });

    this.closeEls.forEach((el) => {
      el.addEventListener('click', this.handleClose);
    });

    this.lightboxVideo.addEventListener(
      'loadedmetadata',
      this.handleLightboxMetadata
    );

    return this;
  }

  /**
   * Tears the module down, removing every listener it attached, releasing any
   * playing source and putting the markup back the way the template rendered
   * it.
   * @returns {VideosAndText} A reference to the current instance of the class
   * @chainable
   */
  destroy() {
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleTriggerClick);
      trigger.removeEventListener('keydown', this.handleTriggerKeydown);
    });

    this.closeEls.forEach((el) => {
      el.removeEventListener('click', this.handleClose);
    });

    if (this.lightboxVideo) {
      this.lightboxVideo.removeEventListener(
        'loadedmetadata',
        this.handleLightboxMetadata
      );
    }

    document.removeEventListener('keydown', this.handleDocumentKeydown);
    this.clearEmbed();
    this.clearPosters();
    this.unlockScroll();
    this.restoreLightbox();

    return this;
  }

  /**
   * Opens the lightbox for the activated trigger, routing to the file or embed
   * player depending on how the block was authored.
   * @param {MouseEvent} event The click event from a trigger block
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  onTriggerClick(event) {
    const trigger = event.currentTarget;

    this.lastFocused = trigger;

    if (VideosAndText.isEmbed(trigger)) {
      this.openEmbed(trigger);
    } else {
      // The in-grid video preloads metadata, so its dimensions are usually
      // known already. Applying them up front avoids a 16:9 flash before the
      // real source reports its own size.
      this.setRatio(VideosAndText.getRatio(trigger.querySelector('video')));
      this.open(VideosAndText.getVideoSrc(trigger));
    }

    return this;
  }

  /**
   * Mirrors click activation for keyboard users, since the trigger carries
   * role="button".
   * @param {KeyboardEvent} event The keydown event from a trigger block
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  onTriggerKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onTriggerClick(event);
    }

    return this;
  }

  /**
   * Closes the lightbox when the Escape key is pressed.
   * @param {KeyboardEvent} event The document keydown event
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  onDocumentKeydown(event) {
    if (event.key === 'Escape') this.close();

    return this;
  }

  /**
   * Re-measures the lightbox once the real source reports its dimensions.
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  onLightboxMetadata() {
    this.setRatio(VideosAndText.getRatio(this.lightboxVideo));

    return this;
  }

  /**
   * Publishes the video's aspect ratio to CSS as `--vt-ratio` and flags the
   * orientation, letting the stylesheet size the player around any source.
   * @param {?number} ratio Intrinsic width / height, or null to reset
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  setRatio(ratio) {
    const value = ratio && Number.isFinite(ratio) ? ratio : DEFAULT_RATIO;

    this.lightbox.style.setProperty('--vt-ratio', String(value));
    this.lightbox.classList.toggle(
      CLASS_NAMES.VIDEOS_AND_TEXT_LIGHTBOX_PORTRAIT,
      value < 1
    );

    return this;
  }

  /**
   * Swaps which player the lightbox exposes, keeping the idle one out of the
   * accessibility tree as well as out of view.
   * @param {boolean} isEmbed True to show the iframe host, false for `<video>`
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  setPlayer(isEmbed) {
    if (this.lightboxVideo) this.lightboxVideo.hidden = isEmbed;
    if (this.lightboxEmbed) this.lightboxEmbed.hidden = !isEmbed;

    return this;
  }

  /**
   * Reveals the overlay, locks scrolling, starts listening for Escape and moves
   * focus to the close button. Shared by both player types.
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  reveal() {
    this.lightbox.classList.add(SELECTORS.IS_ACTIVE);
    this.lightbox.setAttribute('aria-hidden', 'false');

    this.lockScroll();
    document.addEventListener('keydown', this.handleDocumentKeydown);

    const closeBtn = this.closeEls.find((el) => el.tagName === 'BUTTON');
    if (closeBtn) closeBtn.focus();

    return this;
  }

  /**
   * Sets the lightbox video source, reveals the overlay, locks scrolling and
   * starts playback.
   * @param {?string} src The video URL to play
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  open(src) {
    if (!src) return this;

    this.setPlayer(false);
    this.lightboxVideo.setAttribute('src', src);

    this.reveal();

    const playPromise = this.lightboxVideo.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {}); // Ignore autoplay rejection; controls remain
    }

    return this;
  }

  /**
   * Builds a live provider iframe from the trigger's inert template and reveals
   * it in the lightbox. The iframe is created fresh on every open rather than
   * reused, so closing can destroy it outright and guarantee playback stops —
   * a cross-origin player cannot be paused from here.
   * @param {HTMLElement} trigger The embed block that was activated
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  openEmbed(trigger) {
    if (!this.lightboxEmbed) return this;

    const frame = VideosAndText.getEmbedFrame(trigger);
    const src = frame
      ? VideosAndText.withAutoplay(frame.getAttribute('src'))
      : null;

    if (!src) return this;

    const iframe = document.createElement('iframe');

    iframe.setAttribute('src', src);
    iframe.setAttribute(
      'title',
      frame.getAttribute('title') ||
        trigger.getAttribute('aria-label') ||
        'Video player'
    );
    iframe.setAttribute('allow', EMBED_ALLOW);
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'eager');

    this.clearEmbed();
    this.setRatio(VideosAndText.getEmbedRatio(frame));
    this.lightboxEmbed.appendChild(iframe);
    this.setPlayer(true);

    this.reveal();

    return this;
  }

  /**
   * Destroys the injected provider iframe, which is the only reliable way to
   * stop a cross-origin player.
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  clearEmbed() {
    if (this.lightboxEmbed) this.lightboxEmbed.innerHTML = '';

    return this;
  }

  /**
   * Hides the overlay, stops and releases whichever player was active, unlocks
   * scrolling and restores focus to the trigger that opened it.
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  close() {
    if (!this.lightbox.classList.contains(SELECTORS.IS_ACTIVE)) {
      return this;
    }

    this.lightbox.classList.remove(SELECTORS.IS_ACTIVE);
    this.lightbox.setAttribute('aria-hidden', 'true');

    this.lightboxVideo.pause();
    this.lightboxVideo.removeAttribute('src');
    this.lightboxVideo.load(); // Stop buffering / release the source

    this.clearEmbed();
    this.setPlayer(false);

    this.setRatio(null);
    this.unlockScroll();
    document.removeEventListener('keydown', this.handleDocumentKeydown);

    if (this.lastFocused) {
      this.lastFocused.focus();
      this.lastFocused = null;
    }

    return this;
  }

  /**
   * Freezes the background by pinning the body at its current scroll offset.
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  lockScroll() {
    this.scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${this.scrollY}px`;
    document.body.classList.add(CLASS_NAMES.VIDEOS_AND_TEXT_SCROLL_LOCK);

    return this;
  }

  /**
   * Releases the background pin and restores the previous scroll offset. Bails
   * out when the lock is not applied, so teardown never resets the scroll
   * position of a page that was not locked.
   * @returns {VideosAndText} A reference to the current instance of the class
   */
  unlockScroll() {
    const { body } = document;

    if (!body.classList.contains(CLASS_NAMES.VIDEOS_AND_TEXT_SCROLL_LOCK)) {
      return this;
    }

    body.classList.remove(CLASS_NAMES.VIDEOS_AND_TEXT_SCROLL_LOCK);
    body.style.top = '';
    window.scrollTo(0, this.scrollY);

    return this;
  }
}
